// ============================================================================
// Recurring window mirror — parent-date-change reconciler.
// ----------------------------------------------------------------------------
// When a Task / Goal / Subtask's startDate or endDate changes, every DAILY /
// WEEKLY checklist whose `parentContainerId` points at that element needs
// its `recurringWindow` mirrored to the new range. The per-date filter in
// ChecklistDrawingBoard.displayNodes then automatically hides instances
// whose date falls outside the new range — old completion pages stay
// on disk (auditable) but become invisible, which matches the archived
// time-status semantics.
//
// This is the "silent" mirror. If a future iteration wants to actively
// delete orphan pages we can layer that on top of this function.
// ============================================================================
import {
  collection,
  deleteField,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import type { ChecklistDefinition } from '../types/checklistBooks'
import { scopedReadConstraints } from './accessScopeService'

const CHECKLISTS_COLLECTION = 'checklists'

const isIsoDate = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)

/**
 * Called after a parent element's startDate / endDate is written. Fires a
 * best-effort update on every DAILY / WEEKLY child so the child's
 * recurringWindow tracks the parent. Silently succeeds when there are no
 * recurring children — cheap enough to call on every date change.
 *
 * Never throws to the caller: parent date writes must not be rolled back
 * because a follow-up mirror failed. Errors are logged.
 */
export const mirrorRecurringWindowsForParent = async (
  parentElementId: string,
  workspaceId: string,
  nextStartDate: string | null | undefined,
  nextEndDate: string | null | undefined,
): Promise<void> => {
  try {
    if (!parentElementId || !workspaceId) return
    const start = isIsoDate(nextStartDate) ? (nextStartDate as string).slice(0, 10) : null
    const end = isIsoDate(nextEndDate) ? (nextEndDate as string).slice(0, 10) : null
    const bothDatesSet = Boolean(start && end && start <= end)

    const q = query(
      collection(firestoreDb, CHECKLISTS_COLLECTION),
      where('workspaceId', '==', workspaceId),
      where('parentContainerId', '==', parentElementId),
      ...scopedReadConstraints(workspaceId),
    )
    const snap = await getDocs(q)
    if (snap.empty) return
    const batch = writeBatch(firestoreDb)
    let touched = 0
    for (const docSnap of snap.docs) {
      const data = docSnap.data() as ChecklistDefinition
      if (data.checklistType !== 'DAILY' && data.checklistType !== 'WEEKLY') continue
      if (bothDatesSet && start && end) {
        const currentStart = data.recurringWindow?.startDate
        const currentEnd = data.recurringWindow?.endDate
        const isPaused = data.repeatPaused === true
        if (currentStart === start && currentEnd === end && !isPaused && !data.repeatPausedReason) continue
        batch.update(docSnap.ref, {
          recurringWindow: {
            parentElementId,
            startDate: start,
            endDate: end,
            syncedAt: serverTimestamp(),
          },
          repeatPaused: deleteField(),
          repeatPausedReason: deleteField(),
          updatedAt: serverTimestamp(),
        })
        touched += 1
      } else {
        if (data.repeatPaused === true && data.repeatPausedReason === 'parent-missing-dates') continue
        batch.update(docSnap.ref, {
          repeatPaused: true,
          repeatPausedReason: 'parent-missing-dates',
          updatedAt: serverTimestamp(),
        })
        touched += 1
      }
    }
    if (touched > 0) await batch.commit()
  } catch (error) {
    console.warn('[mirrorRecurringWindowsForParent] silent failure', {
      parentElementId,
      workspaceId,
      error,
    })
  }
}
